import { useRef } from 'react'
import { Link } from 'react-router-dom'
import {
  FileTextOutlined,
  IconCustomSparkle,
  IconPlus,
  IconStandardBox,
  WarningOutlined,
} from '../../../components/icons'
import { Chip } from '../../../components/ui/Chip'
import { Skeleton } from '../../../components/ui/Skeleton'
import { useIsScrollable } from '../../../components/ui/useIsScrollable'
import { PartRevBand } from '../../parts/components/PartRevBand'
import { PartThumbnail } from '../../parts/components/PartThumbnail'
import { PartRowActions } from '../../parts/components/PartsTable'
import { VolumePricingCellTrigger } from '../../parts/components/VolumePricingPopover'
import {
  formatDate,
  formatPrice,
  formatPriceRange,
  formatRequirementsDetail,
} from '../../parts/format'
import { formatProcessLabel } from '../../parts/process-label'
import { resolveRowStatus } from '../../parts/row-status'
import type { LibraryPartRow } from '../../parts/types'

export interface WorkspacePartsTabProps {
  readonly items: readonly LibraryPartRow[]
  readonly selectedIds: ReadonlySet<string>
  /** UID-610 — `shiftKey` carries the modifier state from the DOM `click`
   * event (mouse AND the Shift+Space keyboard equivalent — a `click` event
   * carries live modifier state for both, same as `PartsTable.tsx`'s
   * precedent), so the caller can resolve a Gmail/Finder range select via
   * the shared `useRangeSelection` hook. */
  readonly onToggleSelect: (id: string, shiftKey: boolean) => void
  readonly onToggleSelectAll: (ids: readonly string[], select: boolean) => void
  readonly onOpen?: ((partId: string) => void) | undefined
  readonly onRequestVolumeRfq: (item: LibraryPartRow) => void
  readonly onEditConfig?: ((item: LibraryPartRow) => void) | undefined
  readonly onReorder?: ((id: string) => void) | undefined
  readonly reorderingId?: string | null | undefined
  /** UID-407 (design-parity audit, finding 5) — same optional-callback shape
   * as `PartsTable`'s `onShare`/`onRemoveFromLibrary`/`onCreateRevision`; the
   * ⋮ menu renders an item only when the caller wires a real handler. */
  readonly onShare?: ((item: LibraryPartRow) => void) | undefined
  readonly onRemoveFromLibrary?: ((item: LibraryPartRow) => void) | undefined
  readonly onCreateRevision?: ((item: LibraryPartRow) => void) | undefined
}

function readConfigAttr(
  attrs: Readonly<Record<string, unknown>> | undefined,
  key: string,
): string | undefined {
  const value = attrs?.[key]
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined
}

function NOOP() {}

/**
 * UID-362 — the Project detail page's Parts tab: a deliberately SIMPLER row
 * than the full Parts Library's `PartsTable` — one Source label (not the
 * Library's status chip + Custom/Standard pair). Reuses the Library's own
 * status/format helpers so the two screens agree on what "ready"/
 * "needs config"/"needs quote" mean and how a price renders.
 *
 * UID-407 (design-parity audit, finding 5) CORRECTED an earlier reading of
 * this screen's design ref: the authoritative comp (`Fictiv Platform.dc.html`
 * `wsView.partItems`, not the non-authoritative `Project Workspace.dc.html`
 * exploration file the original UID-362 comment cited) DOES render a per-row
 * ⋮ "more actions" menu here, identical to the Library's own
 * (`it.rowMenu` / `PartRowActions`) — Share part / Create revision / Remove
 * from library. This screen now reuses that same exported component rather
 * than omitting it.
 */
export function WorkspacePartsTab({
  items,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onOpen,
  onRequestVolumeRfq,
  onEditConfig,
  onReorder,
  reorderingId,
  onShare,
  onRemoveFromLibrary,
  onCreateRevision,
}: WorkspacePartsTabProps) {
  const ids = items.map((item) => item.sourceable.id)
  const allSelected = ids.length > 0 && ids.every((id) => selectedIds.has(id))
  const someSelected = ids.some((id) => selectedIds.has(id)) && !allSelected
  const wrapRef = useRef<HTMLDivElement>(null)
  const isScrollable = useIsScrollable(wrapRef)

  return (
    <div
      ref={wrapRef}
      className={`workspace-parts-tab-table-wrap parts-table-card${isScrollable ? ' is-scrollable' : ''}`}
    >
      <table className="parts-table workspace-parts-table">
        <thead>
          <tr>
            <th scope="col">
              <label className="parts-row-checkbox-hit">
                <input
                  type="checkbox"
                  className="checkbox-input"
                  aria-label="Select all parts in this project"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected
                  }}
                  onChange={(event) => onToggleSelectAll(ids, event.target.checked)}
                />
              </label>
            </th>
            <th scope="col">Part</th>
            <th scope="col">Name &amp; drawing</th>
            <th scope="col">Requirements</th>
            <th scope="col">Source</th>
            <th scope="col">Qty</th>
            <th scope="col">Unit price</th>
            <th scope="col">
              <span className="sr-only">Action</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const id = item.sourceable.id
            const name = item.sourceable.name ?? '(unnamed part)'
            const isCustom = item.sourceable.provenance === 'fictiv_custom'
            const isMisumiStandard = item.sourceable.provenance === 'misumi_standard'
            const busy = reorderingId === id
            const currency = item.sourceable.indicativePrice?.currency ?? 'USD'
            const curve = item.sourceable.pricingProjection?.volumeCurve
            const attrs = item.sourceable.configAttributes
            const reqProcess =
              readConfigAttr(attrs, 'process') ?? item.sourceable.dfmSummary?.suggestedProcess
            const reqMaterial =
              readConfigAttr(attrs, 'material') ?? item.sourceable.dfmSummary?.suggestedMaterial
            const reqTolerance = readConfigAttr(attrs, 'tolerance')
            const reqFinish = readConfigAttr(attrs, 'finish')
            const reqPrimary =
              reqProcess != null ? formatProcessLabel(reqProcess) : item.requirements
            const reqDetail =
              reqProcess != null
                ? formatRequirementsDetail([reqMaterial, reqTolerance, reqFinish])
                : null

            return (
              <tr key={id}>
                <td>
                  <label className="parts-row-checkbox-hit">
                    <input
                      type="checkbox"
                      className="checkbox-input"
                      aria-label={`Select ${name}`}
                      checked={selectedIds.has(id)}
                      // UID-610 — reads `event.shiftKey` off `onClick`;
                      // `onChange` carries no modifier-key state, so it
                      // stays a no-op (see `onToggleSelect`'s doc comment).
                      onClick={(event) => onToggleSelect(id, event.shiftKey)}
                      onChange={() => {}}
                    />
                  </label>
                </td>
                <td>
                  <WorkspacePartThumb thumbnailUrl={item.thumbnailUrl} rev={item.sourceable.rev} />
                </td>
                <td>
                  <div className="parts-row-name">
                    <Link
                      to={`/parts/${id}`}
                      className="parts-row-name-label"
                      onClick={(event) => {
                        event.stopPropagation()
                        onOpen?.(id)
                      }}
                    >
                      {name}
                    </Link>
                    {(item.sourceable.catalogSku || isMisumiStandard) && (
                      <span className="caption-text parts-row-subtitle">
                        {item.sourceable.catalogSku}
                        {item.sourceable.catalogSku && isMisumiStandard && ' · '}
                        {isMisumiStandard && 'MISUMI'}
                      </span>
                    )}
                    {item.drawingFilename && (
                      <span className="parts-row-drawing-chip">
                        <span className="parts-row-drawing-chip-icon">
                          <FileTextOutlined aria-hidden="true" />
                        </span>
                        <span className="parts-row-drawing-chip-label">{item.drawingFilename}</span>
                      </span>
                    )}
                    <span className="caption-text">Updated {formatDate(item.updatedAt)}</span>
                  </div>
                </td>
                <td>
                  <div className="parts-requirements-cell">
                    <span className="parts-requirements-primary-text">{reqPrimary}</span>
                    {reqDetail && <span className="caption-text">{reqDetail}</span>}
                  </div>
                </td>
                <td>
                  <Chip
                    tone={isCustom ? 'success' : 'neutral'}
                    icon={isCustom ? <IconCustomSparkle /> : <IconStandardBox />}
                  >
                    {isCustom ? 'Make' : 'Buy'}
                  </Chip>
                </td>
                <td>
                  {/* UID-407 (finding 6): comp `wsView.partItems[].hasCurve`
                   * shows a "Volume pricing" trigger beside Qty for a row
                   * with a `volumeCurve` — the SAME trigger `PartsTable`'s
                   * price cell hosts, reused verbatim rather than a new
                   * icon. */}
                  {item.quantity}
                  {curve && (
                    <VolumePricingCellTrigger
                      partName={name}
                      quantity={item.quantity}
                      currency={currency}
                      curve={curve}
                      sourceable={item.sourceable}
                      onRequestRfq={() => onRequestVolumeRfq(item)}
                    />
                  )}
                </td>
                <td>
                  <WorkspacePartPriceCell item={item} />
                </td>
                <td>
                  <div className="workspace-parts-row-actions-cell">
                    <WorkspacePartPrimaryAction
                      item={item}
                      name={name}
                      busy={busy}
                      onAddToCart={onReorder ? () => onReorder(id) : undefined}
                      onConfigure={onEditConfig ? () => onEditConfig(item) : undefined}
                      onRequestQuote={() => onRequestVolumeRfq(item)}
                    />
                    {/* UID-654 — `PartRowActions` now takes the raw `item` +
                        top-level `onShare`/`onRemoveFromLibrary`/
                        `onCreateRevision` (binds internally via
                        `useCallback`), not a per-row closure built here —
                        see that component's doc comment in `PartsTable.tsx`. */}
                    <PartRowActions
                      item={item}
                      partName={name}
                      reordering={busy}
                      onShare={onShare}
                      onRemoveFromLibrary={onRemoveFromLibrary}
                      onCreateRevision={onCreateRevision}
                    />
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function WorkspacePartThumb({
  thumbnailUrl,
  rev,
}: {
  readonly thumbnailUrl: string | null
  readonly rev?: string | null | undefined
}) {
  const decorative = !thumbnailUrl && !rev
  return (
    <div className="parts-row-thumb" aria-hidden={decorative || undefined}>
      <PartThumbnail url={thumbnailUrl} />
      <PartRevBand rev={rev} />
    </div>
  )
}

const PRIMARY_ACTION_LABEL: Record<'ready' | 'needs_config' | 'needs_quote', string> = {
  ready: 'Add to cart',
  needs_config: 'Configure',
  needs_quote: 'Request quote',
}

/** Single dynamic action button per row (design ref
 * `Project Workspace.dc.html:90-150`, `it.actionLabel`/`it.actionClass`/
 * `it.onAction`) — mirrors `PartsTable.tsx`'s `PartPrimaryAction` exactly so
 * the two screens agree on what each row status does, minus that
 * component's overflow-menu sibling this screen's design doesn't call for. */
function WorkspacePartPrimaryAction({
  item,
  name,
  busy,
  onAddToCart,
  onConfigure,
  onRequestQuote,
}: {
  readonly item: LibraryPartRow
  readonly name: string
  readonly busy: boolean
  readonly onAddToCart?: (() => void) | undefined
  readonly onConfigure?: (() => void) | undefined
  readonly onRequestQuote: () => void
}) {
  const status = resolveRowStatus(item)

  if (status === 'analyzing') {
    return (
      <span className="parts-row-primary-shimmer" aria-busy="true">
        <Skeleton width={72} height={20} />
        <span aria-live="polite">Analyzing…</span>
      </span>
    )
  }

  if (status === 'needs_config') {
    return (
      <button
        type="button"
        className="btn btn-warning btn-sm"
        onClick={onConfigure ?? NOOP}
        title={onConfigure ? undefined : 'Configuration isn’t available for this part yet'}
      >
        <WarningOutlined aria-hidden="true" />
        {PRIMARY_ACTION_LABEL.needs_config}
      </button>
    )
  }

  if (status === 'needs_quote') {
    return (
      <button type="button" className="btn btn-outline-purple btn-sm" onClick={onRequestQuote}>
        {PRIMARY_ACTION_LABEL.needs_quote}
      </button>
    )
  }

  return (
    <button
      type="button"
      className="icon-btn parts-row-add-btn"
      aria-label={`Add ${name} to cart`}
      aria-busy={busy}
      disabled={busy}
      onClick={onAddToCart ?? NOOP}
      title={onAddToCart ? undefined : 'Adding to cart isn’t available for this part yet'}
    >
      <IconPlus size={18} strokeWidth={2.2} />
    </button>
  )
}

function WorkspacePartPriceCell({ item }: { readonly item: LibraryPartRow }) {
  const status = resolveRowStatus(item)
  const currency = item.sourceable.indicativePrice?.currency ?? 'USD'
  const tiers = item.sourceable.pricingProjection?.volumeCurve?.tiers

  if (status === 'analyzing') {
    return (
      <span className="parts-price-cell" aria-busy="true">
        <Skeleton width={64} height={16} />
      </span>
    )
  }

  if (status === 'needs_quote') {
    return (
      <span className="parts-price-cell">
        <span className="parts-price-main parts-price-state-quote">Request quote</span>
        {item.inRfq && <Chip tone="violet">In RFQ</Chip>}
      </span>
    )
  }

  if (status === 'needs_config') {
    return (
      <span className="parts-price-cell">
        <span className="parts-price-main parts-price-state-config">Config to price</span>
      </span>
    )
  }

  const priceLabel =
    tiers && tiers.length > 0
      ? formatPriceRange(currency, tiers)
      : item.sourceable.indicativePrice
        ? formatPrice(item.sourceable.indicativePrice)
        : null

  return (
    <span className="parts-price-cell">
      {priceLabel ? (
        <span className="parts-price-main">{priceLabel}</span>
      ) : (
        <span className="caption-text">—</span>
      )}
      {item.inRfq && <Chip tone="violet">In RFQ</Chip>}
    </span>
  )
}
